import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getInventoryApi, bulkUpdateInventoryApi, downloadReportApi } from '../../api/adminApi';
import { updateInventoryApi } from '../../api/productApi';
import imgUrl from '../../utils/imgUrl';

export default function InventoryTab() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [edits, setEdits]       = useState({});
  const [saving, setSaving]     = useState(false);

  const load = () => {
    setLoading(true);
    getInventoryApi()
      .then((r) => { setItems(r.data); setEdits({}); })
      .catch(() => toast.error('Failed to load inventory'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = items.filter((it) =>
    !search || it.name?.toLowerCase().includes(search.toLowerCase()) || it.sku?.toLowerCase().includes(search.toLowerCase())
  );

  const lowStock  = items.filter((it) => (it.stock || 0) > 0 && (it.stock || 0) <= 5);
  const outStock  = items.filter((it) => (it.stock || 0) === 0);
  const inStock   = items.filter((it) => (it.stock || 0) > 5);

  const setEdit = (id, val) => setEdits((prev) => ({ ...prev, [id]: val }));

  const saveOne = async (id, current) => {
    const qty = parseInt(edits[id]);
    if (isNaN(qty) || qty < 0) return toast.error('Invalid quantity');
    if (qty === current) return;
    try {
      await updateInventoryApi(id, { quantity: qty });
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, stock: qty } : it));
      setEdits((prev) => { const n = { ...prev }; delete n[id]; return n; });
      toast.success('Stock updated');
    } catch { toast.error('Failed to update'); }
  };

  const saveBulk = async () => {
    const updates = Object.entries(edits).map(([product_id, quantity]) => ({
      product_id,
      quantity: parseInt(quantity),
    })).filter(({ quantity }) => !isNaN(quantity) && quantity >= 0);

    if (!updates.length) return toast.info('No pending changes');
    setSaving(true);
    try {
      await bulkUpdateInventoryApi(updates);
      setItems((prev) => prev.map((it) => {
        const upd = updates.find((u) => u.product_id === it.id);
        return upd ? { ...it, stock: upd.quantity } : it;
      }));
      setEdits({});
      toast.success(`${updates.length} products updated`);
    } catch { toast.error('Bulk update failed'); }
    finally { setSaving(false); }
  };

  const downloadReport = async () => {
    try {
      const r = await downloadReportApi('inventory');
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'inventory-report.csv'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const pendingCount = Object.keys(edits).length;

  return (
    <div>
      {/* Header */}
      <div style={S.hdr}>
        <div>
          <h1 style={S.title}>Inventory</h1>
          <p style={S.sub}>{items.length} products · {outStock.length} out of stock · {lowStock.length} low stock</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {pendingCount > 0 && (
            <button style={S.btnPrimary} onClick={saveBulk} disabled={saving}>
              {saving ? 'Saving...' : `Save ${pendingCount} Change${pendingCount > 1 ? 's' : ''}`}
            </button>
          )}
          <button style={S.btnGhost} onClick={downloadReport}>↓ Export CSV</button>
        </div>
      </div>

      {/* KPI Strip */}
      <div style={S.kpiRow}>
        {[
          { label: 'Total Products', val: items.length, color: '#EDE9FE', fg: '#6D28D9' },
          { label: 'In Stock',       val: inStock.length, color: '#DCFCE7', fg: '#16A34A' },
          { label: 'Low Stock (≤5)', val: lowStock.length, color: '#FEF3C7', fg: '#D97706' },
          { label: 'Out of Stock',   val: outStock.length, color: '#FEE2E2', fg: '#DC2626' },
        ].map((k) => (
          <div key={k.label} style={{ ...S.kpiCard, background: k.color }}>
            <p style={{ ...S.kpiLabel, color: k.fg }}>{k.label}</p>
            <p style={{ ...S.kpiVal, color: k.fg }}>{k.val}</p>
          </div>
        ))}
      </div>

      {/* Low stock alert */}
      {(lowStock.length > 0 || outStock.length > 0) && (
        <div style={S.alert}>
          <span style={{ fontWeight: 700, color: '#D97706' }}>⚠ Stock Alert:</span>
          {' '}{outStock.length} products are out of stock and {lowStock.length} are running low.
          Update quantities below or{' '}
          <button style={S.alertLink} onClick={() => setSearch('')}>view all</button>.
        </div>
      )}

      {/* Search */}
      <div style={S.filterBar}>
        <div style={S.searchWrap}>
          <span style={S.searchIco}>🔍</span>
          <input style={S.searchInp} placeholder="Search by product name or SKU..." value={search}
            onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {/* Table */}
      <div style={S.tableWrap}>
        <table style={S.table}>
          <thead>
            <tr>
              {['Product', 'SKU', 'Category', 'Price', 'Stock Level', 'Status', 'Update Stock'].map((h) => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading
              ? <tr><td colSpan={7} style={S.empty}>Loading...</td></tr>
              : filtered.length === 0
                ? <tr><td colSpan={7} style={S.empty}>No products found</td></tr>
                : filtered.map((it) => {
                    const stock   = it.stock || 0;
                    const maxBar  = Math.max(...filtered.map((x) => x.stock || 0), 1);
                    const barPct  = Math.min((stock / maxBar) * 100, 100);
                    const barClr  = stock === 0 ? '#EF4444' : stock <= 5 ? '#F59E0B' : '#16A34A';
                    const editVal = edits[it.id] !== undefined ? edits[it.id] : stock;
                    const changed = edits[it.id] !== undefined && parseInt(edits[it.id]) !== stock;

                    return (
                      <tr key={it.id} style={{ ...S.tr, background: changed ? '#FFFBEB' : '#fff' }}>
                        <td style={S.td}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {it.image
                              ? <img src={imgUrl(it.image)} alt={it.name} style={S.thumb} />
                              : <div style={S.thumbEmpty}>🏷</div>
                            }
                            <span style={{ fontWeight: 700, color: '#111', fontSize: '13px' }}>{it.name}</span>
                          </div>
                        </td>
                        <td style={S.td}><span style={S.sku}>{it.sku || '—'}</span></td>
                        <td style={S.td}>{it.category_name}</td>
                        <td style={S.td}><strong>₹{parseFloat(it.price).toLocaleString('en-IN')}</strong></td>
                        <td style={{ ...S.td, width: '160px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: '6px', background: '#F3F4F6', borderRadius: '99px' }}>
                              <div style={{ height: '100%', width: `${barPct}%`, background: barClr, borderRadius: '99px', transition: 'width .3s' }} />
                            </div>
                            <span style={{ fontSize: '12px', fontWeight: 700, color: barClr, minWidth: '28px', textAlign: 'right' }}>{stock}</span>
                          </div>
                        </td>
                        <td style={S.td}>
                          <span style={{ ...S.pill,
                            background: stock === 0 ? '#FEE2E2' : stock <= 5 ? '#FEF3C7' : '#DCFCE7',
                            color:      stock === 0 ? '#DC2626' : stock <= 5 ? '#D97706' : '#16A34A' }}>
                            {stock === 0 ? 'Out of Stock' : stock <= 5 ? 'Low Stock' : 'In Stock'}
                          </span>
                        </td>
                        <td style={S.td}>
                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                            <input
                              type="number" min="0" step="1"
                              style={{ ...S.qtyInput, borderColor: changed ? '#D97706' : '#E5E5E5' }}
                              value={editVal}
                              onChange={(e) => setEdit(it.id, e.target.value)}
                            />
                            {changed && (
                              <button style={S.saveBtn} onClick={() => saveOne(it.id, stock)}>✓</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
            }
          </tbody>
        </table>
      </div>
    </div>
  );
}

const S = {
  hdr:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '16px' },
  title:      { fontFamily: 'var(--font-display)', fontSize: '24px', letterSpacing: '1.5px', color: '#111', margin: 0 },
  sub:        { fontSize: '13px', color: '#9CA3AF', marginTop: '2px' },
  btnPrimary: { background: '#111', color: '#fff', border: 'none', padding: '9px 18px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer', letterSpacing: '.5px' },
  btnGhost:   { background: '#fff', color: '#333', border: '1.5px solid #E5E5E5', padding: '8px 16px', fontFamily: 'var(--font-body)', fontSize: '12px', fontWeight: 700, cursor: 'pointer' },
  kpiRow:     { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px', marginBottom: '16px' },
  kpiCard:    { padding: '16px', border: '1px solid transparent' },
  kpiLabel:   { fontSize: '10px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '4px' },
  kpiVal:     { fontFamily: 'var(--font-display)', fontSize: '26px', letterSpacing: '1px', margin: 0 },
  alert:      { background: '#FFFBEB', border: '1px solid #FDE68A', padding: '12px 16px', fontSize: '13px', color: '#92400E', marginBottom: '14px' },
  alertLink:  { background: 'none', border: 'none', color: '#D97706', fontWeight: 700, cursor: 'pointer', fontSize: '13px', textDecoration: 'underline', padding: 0 },
  filterBar:  { marginBottom: '12px' },
  searchWrap: { position: 'relative', maxWidth: '400px' },
  searchIco:  { position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '13px' },
  searchInp:  { width: '100%', border: '1.5px solid #E5E5E5', background: '#fff', padding: '9px 12px 9px 36px', fontFamily: 'var(--font-body)', fontSize: '13px', outline: 'none', boxSizing: 'border-box' },
  tableWrap:  { background: '#fff', border: '1px solid #E5E5E5', overflow: 'auto' },
  table:      { width: '100%', borderCollapse: 'collapse', minWidth: '900px' },
  th:         { padding: '10px 14px', fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', textTransform: 'uppercase', color: '#9CA3AF', borderBottom: '1px solid #E5E5E5', textAlign: 'left', background: '#FAFAFA', whiteSpace: 'nowrap' },
  tr:         { borderBottom: '1px solid #F9FAFB', transition: 'background .1s' },
  td:         { padding: '10px 14px', fontSize: '13px', verticalAlign: 'middle' },
  empty:      { textAlign: 'center', padding: '48px', color: '#aaa', fontSize: '14px' },
  thumb:      { width: '36px', height: '36px', objectFit: 'cover', flexShrink: 0, border: '1px solid #E5E5E5' },
  thumbEmpty: { width: '36px', height: '36px', background: '#F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', flexShrink: 0 },
  sku:        { fontFamily: 'var(--font-display)', fontSize: '11px', letterSpacing: '1px', color: '#6B7280', background: '#F3F4F6', padding: '2px 7px' },
  pill:       { display: 'inline-block', fontSize: '10px', fontWeight: 700, padding: '3px 9px', borderRadius: '99px', whiteSpace: 'nowrap' },
  qtyInput:   { width: '70px', border: '1.5px solid #E5E5E5', padding: '6px 8px', fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 700, textAlign: 'center', outline: 'none' },
  saveBtn:    { background: '#16A34A', color: '#fff', border: 'none', width: '28px', height: '28px', cursor: 'pointer', fontWeight: 700, fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
